// src/components/appointment/AppointmentForm.jsx

import React, { useState } from "react";
import {
  CheckCircle2,
  UserRound,
  CalendarDays,
  CarFront,
  MessageSquareText,
  Phone,
  Mail,
  Clock3,
  IndianRupee,
  FileText,
  Loader2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

import { appointmentService } from "../../services/appointmentService";


// ============================================================
// FIELD
// ============================================================

const Field = ({
  label,
  children,
  required = false,
  icon: Icon,
}) => (
  <label className="block">
    <span
      className="
        mb-1.5
        flex
        items-center
        gap-1.5
        text-xs
        font-semibold
        text-[#061A3A]
      "
    >
      {Icon && (
        <Icon
          className="h-3.5 w-3.5 text-[#64748B]"
          aria-hidden="true"
        />
      )}

      {label}

      {required && (
        <span className="text-red-500" aria-hidden="true">
          *
        </span>
      )}
    </span>

    {children}
  </label>
);


// ============================================================
// INPUT STYLE
// ============================================================

const inputCls = `
  h-12
  w-full
  rounded-xl
  border
  border-[#E2E8E5]
  bg-white
  px-3.5
  text-sm
  text-[#061A3A]
  shadow-sm
  outline-none
  transition-all
  duration-200

  placeholder:text-[#94A3B8]

  hover:border-[#CBD5E1]

  focus:border-[#00A260]
  focus:ring-4
  focus:ring-[#00A260]/10

  disabled:cursor-not-allowed
  disabled:bg-[#F1F5F3]
  disabled:opacity-70
`;


// ============================================================
// SECTION HEADER
// ============================================================

const SectionHeader = ({
  icon: Icon,
  title,
  description,
}) => (
  <div className="mb-4 flex items-start gap-3">
    <div
      className="
        flex
        h-10
        w-10
        shrink-0
        items-center
        justify-center
        rounded-xl
        bg-[#00A260]/10
        text-[#00A260]
      "
    >
      <Icon
        className="h-5 w-5"
        aria-hidden="true"
      />
    </div>

    <div>
      <h4 className="text-sm font-bold text-[#061A3A] sm:text-base">
        {title}
      </h4>

      {description && (
        <p className="mt-0.5 text-xs leading-5 text-[#64748B]">
          {description}
        </p>
      )}
    </div>
  </div>
);


// ============================================================
// APPOINTMENT TYPES
// ============================================================

const appointmentTypes = [
  {
    value: "Buy a Vehicle",
    label: "Buy a Vehicle",
  },
  {
    value: "Sell a Vehicle",
    label: "Sell a Vehicle",
  },
  {
    value: "Vehicle Exchange",
    label: "Vehicle Exchange",
  },
  {
    value: "Vehicle Consultancy",
    label: "Consultancy",
  },
  {
    value: "Vehicle Inspection",
    label: "Inspection",
  },
  {
    value: "Other",
    label: "Other",
  },
];


// ============================================================
// APPOINTMENT FORM
// ============================================================

const AppointmentForm = ({ onCancel }) => {
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    whatsapp: "",
    email: "",
    appointment_date: "",
    appointment_time: "",
    appointment_type: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_year: "",
    expected_price: "",
    registration_number: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // ==========================================================
  // UPDATE
  // ==========================================================

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // ==========================================================
  // PHONE VALIDATION
  // ==========================================================

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, "");

    return cleaned.length === 10;
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    // Required fields
    if (
      !form.customer_name.trim() ||
      !form.phone.trim()
    ) {
      setError(
        "Please enter your name and phone number."
      );

      return;
    }

    // Phone validation
    if (!isValidPhone(form.phone)) {
      setError(
        "Please enter a valid 10-digit mobile number."
      );

      return;
    }

    // Appointment type
    if (!form.appointment_type) {
      setError(
        "Please select an appointment type."
      );

      return;
    }

    setSubmitting(true);

    try {
      await appointmentService.createAppointment(form);

      setSuccess(true);
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "Unable to submit your appointment request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };


  // ==========================================================
  // SUCCESS
  // ==========================================================

  if (success) {
    return (
      <div
        className="
          flex
          min-h-[360px]
          flex-col
          items-center
          justify-center
          px-4
          py-10
          text-center
          motion-safe:animate-[njvSuccessIn_500ms_cubic-bezier\(0\.22\,1\,0\.36\,1\)]
        "
      >
        {/* Success icon */}

        <div
          className="
            relative
            mb-5
            flex
            h-20
            w-20
            items-center
            justify-center
            rounded-full
            bg-[#00A260]/10
            text-[#00A260]
          "
        >
          <div
            className="
              absolute
              inset-0
              rounded-full
              border
              border-[#00A260]/20
            "
          />

          <CheckCircle2
            className="h-10 w-10"
            strokeWidth={1.8}
          />
        </div>

        <span
          className="
            mb-2
            rounded-full
            bg-[#00A260]/10
            px-3
            py-1
            text-[10px]
            font-bold
            uppercase
            tracking-[0.15em]
            text-[#008C54]
          "
        >
          Request Received
        </span>

        <h3
          className="
            text-xl
            font-extrabold
            tracking-tight
            text-[#061A3A]
            sm:text-2xl
          "
        >
          Appointment Request Sent
        </h3>

        <p
          className="
            mt-2
            max-w-sm
            text-sm
            leading-6
            text-[#64748B]
          "
        >
          Thank you for contacting NJV Motors.
          Our team will review your request and
          contact you shortly.
        </p>

        <button
          type="button"
          onClick={onCancel}
          className="
            mt-7
            inline-flex
            h-11
            items-center
            justify-center
            gap-2
            rounded-xl
            bg-[#00A260]
            px-6
            text-sm
            font-bold
            text-white
            shadow-sm
            transition-all
            duration-200

            hover:bg-[#005543]
            hover:shadow-md

            focus:outline-none
            focus-visible:ring-4
            focus-visible:ring-[#00A260]/20

            active:scale-[0.98]
          "
        >
          Done

          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }


  // ==========================================================
  // FORM
  // ==========================================================

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit}
      noValidate
    >

      {/* ======================================================
          INTRO
      ======================================================= */}

      <div
        className="
          rounded-2xl
          border
          border-[#E2E8E5]
          bg-white
          p-4
          shadow-sm
          sm:p-5
        "
      >
        <div className="flex items-start gap-3">
          <div
            className="
              flex
              h-9
              w-9
              shrink-0
              items-center
              justify-center
              rounded-xl
              bg-[#061A3A]
              text-[#8BF108]
            "
          >
            <CalendarDays className="h-4 w-4" />
          </div>

          <div>
            <p className="text-sm font-bold text-[#061A3A]">
              Let's plan your visit
            </p>

            <p className="mt-1 text-xs leading-5 text-[#64748B]">
              Share a few details and our NJV Motors
              team will help you with your requirement.
            </p>
          </div>
        </div>
      </div>


      {/* ======================================================
          PERSONAL DETAILS
      ======================================================= */}

      <section
        className="
          rounded-2xl
          border
          border-[#E2E8E5]
          bg-white
          p-4
          shadow-sm
          sm:p-5
        "
      >
        <SectionHeader
          icon={UserRound}
          title="Your Details"
          description="How can we contact you?"
        />

        <div className="grid gap-4 sm:grid-cols-2">

          <Field
            label="Full Name"
            required
            icon={UserRound}
          >
            <input
              type="text"
              className={inputCls}
              placeholder="Enter your name"
              value={form.customer_name}
              onChange={(e) =>
                update(
                  "customer_name",
                  e.target.value
                )
              }
              autoComplete="name"
              required
            />
          </Field>


          <Field
            label="Phone Number"
            required
            icon={Phone}
          >
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className={inputCls}
              placeholder="10-digit mobile number"
              value={form.phone}
              onChange={(e) =>
                update(
                  "phone",
                  e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 10)
                )
              }
              autoComplete="tel"
              required
            />
          </Field>


          <Field
            label="WhatsApp"
            icon={Phone}
          >
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className={inputCls}
              placeholder="WhatsApp number"
              value={form.whatsapp}
              onChange={(e) =>
                update(
                  "whatsapp",
                  e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 10)
                )
              }
              autoComplete="tel"
            />
          </Field>


          <Field
            label="Email Address"
            icon={Mail}
          >
            <input
              type="email"
              className={inputCls}
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) =>
                update(
                  "email",
                  e.target.value
                )
              }
              autoComplete="email"
            />
          </Field>

        </div>
      </section>


      {/* ======================================================
          APPOINTMENT DETAILS
      ======================================================= */}

      <section
        className="
          rounded-2xl
          border
          border-[#E2E8E5]
          bg-white
          p-4
          shadow-sm
          sm:p-5
        "
      >
        <SectionHeader
          icon={CalendarDays}
          title="Appointment Details"
          description="When would you like to visit us?"
        />

        <div className="grid gap-4 sm:grid-cols-2">

          <Field
            label="Preferred Date"
            icon={CalendarDays}
          >
            <input
              type="date"
              className={inputCls}
              value={form.appointment_date}
              min={
                new Date()
                  .toISOString()
                  .split("T")[0]
              }
              onChange={(e) =>
                update(
                  "appointment_date",
                  e.target.value
                )
              }
            />
          </Field>


          <Field
            label="Preferred Time"
            icon={Clock3}
          >
            <input
              type="time"
              className={inputCls}
              value={form.appointment_time}
              onChange={(e) =>
                update(
                  "appointment_time",
                  e.target.value
                )
              }
            />
          </Field>

        </div>


        {/* Appointment type */}

        <div className="mt-5">
          <div className="mb-2 flex items-center gap-1.5">
            <span className="text-xs font-semibold text-[#061A3A]">
              What do you need?
            </span>

            <span className="text-red-500">
              *
            </span>
          </div>

          <div
            className="
              grid
              grid-cols-2
              gap-2
              sm:grid-cols-3
            "
          >
            {appointmentTypes.map((type) => {
              const selected =
                form.appointment_type ===
                type.value;

              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() =>
                    update(
                      "appointment_type",
                      type.value
                    )
                  }
                  className={`
                    min-h-[48px]
                    rounded-xl
                    border
                    px-3
                    py-2
                    text-left
                    text-xs
                    font-semibold
                    transition-all
                    duration-200

                    ${
                      selected
                        ? `
                          border-[#00A260]
                          bg-[#00A260]/10
                          text-[#005543]
                          shadow-sm
                        `
                        : `
                          border-[#E2E8E5]
                          bg-white
                          text-[#475569]
                          hover:border-[#00A260]/40
                          hover:bg-[#F8FAF9]
                        `
                    }

                    focus:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-[#00A260]/30
                  `}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={`
                        flex
                        h-4
                        w-4
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border

                        ${
                          selected
                            ? "border-[#00A260] bg-[#00A260]"
                            : "border-[#CBD5E1]"
                        }
                      `}
                    >
                      {selected && (
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      )}
                    </span>

                    {type.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>


      {/* ======================================================
          VEHICLE DETAILS
      ======================================================= */}

      <section
        className="
          rounded-2xl
          border
          border-[#E2E8E5]
          bg-white
          p-4
          shadow-sm
          sm:p-5
        "
      >
        <SectionHeader
          icon={CarFront}
          title="Vehicle Details"
          description="Tell us about the vehicle"
        />

        <div className="grid gap-4 sm:grid-cols-2">

          <Field label="Vehicle Brand">
            <input
              type="text"
              className={inputCls}
              placeholder="e.g. Maruti"
              value={form.vehicle_brand}
              onChange={(e) =>
                update(
                  "vehicle_brand",
                  e.target.value
                )
              }
            />
          </Field>


          <Field label="Vehicle Model">
            <input
              type="text"
              className={inputCls}
              placeholder="e.g. Ertiga"
              value={form.vehicle_model}
              onChange={(e) =>
                update(
                  "vehicle_model",
                  e.target.value
                )
              }
            />
          </Field>


          <Field label="Manufacturing Year">
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              className={inputCls}
              placeholder="e.g. 2019"
              value={form.vehicle_year}
              onChange={(e) =>
                update(
                  "vehicle_year",
                  e.target.value
                )
              }
            />
          </Field>


          <Field
            label="Expected Price"
            icon={IndianRupee}
          >
            <div className="relative">
              <span
                className="
                  pointer-events-none
                  absolute
                  left-3.5
                  top-1/2
                  -translate-y-1/2
                  text-sm
                  font-semibold
                  text-[#64748B]
                "
              >
                ₹
              </span>

              <input
                type="number"
                min="0"
                className={`${inputCls} pl-8`}
                placeholder="e.g. 500000"
                value={form.expected_price}
                onChange={(e) =>
                  update(
                    "expected_price",
                    e.target.value
                  )
                }
              />
            </div>
          </Field>


          <div className="sm:col-span-2">
            <Field label="Registration Number">
              <input
                type="text"
                className={inputCls}
                placeholder="e.g. TN 01 AB 1234"
                value={
                  form.registration_number
                }
                onChange={(e) =>
                  update(
                    "registration_number",
                    e.target.value.toUpperCase()
                  )
                }
              />
            </Field>
          </div>

        </div>
      </section>


      {/* ======================================================
          MESSAGE
      ======================================================= */}

      <section
        className="
          rounded-2xl
          border
          border-[#E2E8E5]
          bg-white
          p-4
          shadow-sm
          sm:p-5
        "
      >
        <SectionHeader
          icon={MessageSquareText}
          title="Additional Information"
          description="Anything else we should know?"
        />

        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[#061A3A]">
            <FileText className="h-3.5 w-3.5 text-[#64748B]" />
            Message
          </span>

          <textarea
            rows={4}
            className="
              w-full
              resize-none
              rounded-xl
              border
              border-[#E2E8E5]
              bg-white
              p-3.5
              text-sm
              leading-6
              text-[#061A3A]
              shadow-sm
              outline-none
              transition-all
              duration-200

              placeholder:text-[#94A3B8]

              hover:border-[#CBD5E1]

              focus:border-[#00A260]
              focus:ring-4
              focus:ring-[#00A260]/10
            "
            placeholder="Tell us about your requirement..."
            value={form.message}
            onChange={(e) =>
              update(
                "message",
                e.target.value
              )
            }
          />
        </label>
      </section>


      {/* ======================================================
          ERROR
      ======================================================= */}

      {error && (
        <div
          role="alert"
          className="
            flex
            items-start
            gap-3
            rounded-xl
            border
            border-red-200
            bg-red-50
            px-4
            py-3
            text-sm
            text-red-700
          "
        >
          <AlertCircle
            className="
              mt-0.5
              h-4
              w-4
              shrink-0
            "
          />

          <span>
            {error}
          </span>
        </div>
      )}


      {/* ======================================================
          ACTIONS
      ======================================================= */}

      <div
        className="
          sticky
          bottom-0
          -mx-4
          flex
          flex-col
          gap-2
          border-t
          border-[#E2E8E5]
          bg-[#F8FAF9]/95
          px-4
          pb-[max(0.5rem,env(safe-area-inset-bottom))]
          pt-4
          backdrop-blur-md

          sm:static
          sm:mx-0
          sm:flex-row
          sm:justify-end
          sm:border-0
          sm:bg-transparent
          sm:p-0
          sm:pt-1
        "
      >

        {/* Cancel */}

        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="
            h-12
            rounded-xl
            border
            border-[#E2E8E5]
            bg-white
            px-5
            text-sm
            font-semibold
            text-[#061A3A]
            transition-all
            duration-200

            hover:bg-[#F1F5F3]
            hover:border-[#CBD5E1]

            focus:outline-none
            focus-visible:ring-2
            focus-visible:ring-[#00A260]/30

            disabled:cursor-not-allowed
            disabled:opacity-50

            sm:h-11
          "
        >
          Cancel
        </button>


        {/* Submit */}

        <button
          type="submit"
          disabled={submitting}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-xl
            bg-[#00A260]
            px-6
            text-sm
            font-bold
            text-white
            shadow-sm
            transition-all
            duration-200

            hover:bg-[#005543]
            hover:shadow-md

            focus:outline-none
            focus-visible:ring-4
            focus-visible:ring-[#00A260]/20

            active:scale-[0.98]

            disabled:cursor-not-allowed
            disabled:opacity-60

            sm:h-11
          "
        >
          {submitting ? (
            <>
              <Loader2
                className="
                  h-4
                  w-4
                  animate-spin
                "
              />

              Sending Request...
            </>
          ) : (
            <>
              Book Appointment

              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>


      {/* ======================================================
          PRIVACY NOTE
      ======================================================= */}

      <p
        className="
          flex
          items-center
          justify-center
          gap-1.5
          pb-2
          text-center
          text-[10px]
          leading-4
          text-[#94A3B8]
        "
      >
        <ShieldIcon />

        Your information is used only to contact
        you about your request.
      </p>
    </form>
  );
};


// ============================================================
// SMALL SHIELD ICON
// ============================================================

const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-3.5 w-3.5 shrink-0"
    aria-hidden="true"
  >
    <path
      d="M12 3L19 6V11C19 15.5 16.1 19.3 12 21C7.9 19.3 5 15.5 5 11V6L12 3Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


export default AppointmentForm;